import { useState } from "react";
import styled from "styled-components";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { useNavigate } from "react-router-dom";

import { COUNTRIES, COUNTRY_ICONS } from "constants/countries";
import * as css from "styles/CssVariables.js";
import globalConstants from "constants/globalConstants";
import { dateFormatted, getTimeAMPM } from "modules/functions";
import ButtonRoundIcon from "components/buttons/ButtonRoundIcon";

import binIcon from "assets/img/icons/bin-icon.svg";
import binWhiteIcon from "assets/img/icons/bin-icon-white.svg";
import copyIcon from "assets/img/icons/copy-icon.svg";
import copyWhiteIcon from "assets/img/icons/copy-white-icon.svg";
import checkIcon from "assets/img/icons/icons8-check.svg";
import checkWhiteIcon from "assets/img/icons/icons8-check-white.svg";
import circleGreyIcon from "assets/img/icons/icons8-circle-grey.svg";
import rightUpArrowIcon from "assets/img/icons/icons8-right-up-arrow.svg";
import rightUpArrowWhiteIcon from "assets/img/icons/icons8-right-up-arrow-white.svg";
import listIcon from "assets/img/icons/list-icon.svg";
import listWhiteIcon from "assets/img/icons/list-icon-white.svg";

const LeadDetail = styled.div`
  position: relative;
  width: 100%;
  padding: ${css.BOX_PADDING_PRIMARY};
  box-sizing: border-box;

  background: ${css.BLUE_BOX_GRADIENT};
  border-radius: ${css.BOX_RADIUS_PRIMARY};

  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const DataColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_LARGE};
`;

const ControlsColumn = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Data = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DataTitle = styled.div`
  font-weight: 500;

  display: flex;
  align-items: center;
  gap: 6px;
`;

const DataRow = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
`;

const DataIconBox = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const DataIcon = styled.div`
  width: ${(props) => (props.width ? props.width : "20px")};
  min-width: ${(props) => (props.width ? props.width : "20px")};
  height: ${(props) => (props.width ? props.width : "20px")};
`;

const DataText = styled.div`
  line-height: 1;
`;

const CopyIconBox = styled.div``;

const Badge = styled.div`
  height: 20px;
  padding: 0 10px 2px;
  min-width: 38px;
  box-sizing: border-box;

  font-size: 13px;
  color: #fff;
  white-space: nowrap;

  border-radius: 10px;
  background: ${(props) =>
    props.verified ? css.POSITIVE_GREEN : css.NEGATIVE_RED};

  display: flex;
  justify-content: center;
  align-items: center;
`;

const CountryIcon = styled.div`
  padding-top: 1px;

  width: 20px;
  min-width: 20px;
  height: 20px;

  &:hover {
    cursor: help;
  }
`;

const DataButtonWrap = styled.div``;

export default ({ data, onDeleteClick, loadingDelete, openPage }) => {
  const navigate = useNavigate();

  const [copied, setCopied] = useState(null);

  const justCopied = (data) => {
    setCopied(data);

    setTimeout(() => setCopied(null), 2000);
  };

  const {
    id,
    created_at,
    name,
    email,
    verified,
    ip_address,
    location_country,
    page,
    responder,
    list,
  } = data.leadDetail;

  let countryIcon = null;
  let countryName = "";

  if (location_country) {
    const country = COUNTRIES[location_country];

    countryName = country.name;

    countryIcon = COUNTRY_ICONS[countryName]
      ? COUNTRY_ICONS[countryName].icon
      : null;
  }

  if (!countryIcon) {
    countryIcon = circleGreyIcon;
    countryName = "Unknown";
  }

  return (
    <LeadDetail>
      <DataColumn>
        <Data>
          <DataTitle>Date</DataTitle>
          <DataRow>
            <DataText>
              {getTimeAMPM(new Date(created_at))},{" "}
              {dateFormatted(new Date(created_at))}
            </DataText>
          </DataRow>
        </Data>
        <Data>
          <DataTitle>Name</DataTitle>
          <DataRow>
            <DataText>{name}</DataText>
            <CopyIconBox>
              <CopyToClipboard text={name} onCopy={() => justCopied("name")}>
                <ButtonRoundIcon
                  icon={copied === "name" ? checkIcon : copyIcon}
                  hoverIcon={copied === "name" ? checkWhiteIcon : copyWhiteIcon}
                  background={css.BRAND_YELLOW}
                  hoverBackground={css.BRAND_GREEN}
                  buttonSize="24px"
                  iconSize="11px"
                />
              </CopyToClipboard>
            </CopyIconBox>
          </DataRow>
        </Data>
        <Data>
          <DataTitle>Email</DataTitle>
          <DataRow>
            <DataText>{email}</DataText>
            <CopyIconBox>
              <CopyToClipboard text={email} onCopy={() => justCopied("email")}>
                <ButtonRoundIcon
                  icon={copied === "email" ? checkIcon : copyIcon}
                  hoverIcon={
                    copied === "email" ? checkWhiteIcon : copyWhiteIcon
                  }
                  background={css.BRAND_YELLOW}
                  hoverBackground={css.BRAND_GREEN}
                  buttonSize="24px"
                  iconSize="11px"
                />
              </CopyToClipboard>
            </CopyIconBox>
          </DataRow>
        </Data>
        <Data>
          <DataTitle>Status</DataTitle>
          <DataRow>
            <DataText>
              {verified && <Badge verified={1}>Verified</Badge>}
              {!verified && <Badge verified={0}>Not verified</Badge>}
            </DataText>
          </DataRow>
        </Data>
        <Data>
          <DataTitle>Country</DataTitle>
          <DataRow>
            <DataIconBox>
              <DataIcon width="18px">
                <CountryIcon>
                  <img src={countryIcon} width="100%" alt={countryName} />
                </CountryIcon>
              </DataIcon>
            </DataIconBox>
            <DataText>{countryName}</DataText>
          </DataRow>
        </Data>
        <Data>
          <DataTitle>IP Address</DataTitle>
          <DataRow>
            <DataText>{ip_address ? ip_address : "Unknown"}</DataText>
          </DataRow>
        </Data>
        <Data>
          <DataTitle>Mailing List</DataTitle>
          <DataRow>
            <DataText>{list.name}</DataText>
            <DataButtonWrap>
              <ButtonRoundIcon
                icon={listIcon}
                hoverIcon={listWhiteIcon}
                background={css.BRAND_YELLOW}
                hoverBackground={css.BRAND_GREEN}
                onClick={() => navigate(`/mailing-list?id=${list.id}`)}
                buttonSize="24px"
                iconSize="11px"
              />
            </DataButtonWrap>
          </DataRow>
        </Data>
        <Data>
          <DataTitle>Landing Page</DataTitle>
          <DataRow>
            <DataText>{page.name}</DataText>
            <DataButtonWrap>
              <ButtonRoundIcon
                icon={rightUpArrowIcon}
                hoverIcon={rightUpArrowWhiteIcon}
                background={css.BRAND_YELLOW}
                hoverBackground={css.BRAND_GREEN}
                onClick={() => openPage(page.code)}
                buttonSize="24px"
                iconSize="11px"
              />
            </DataButtonWrap>
          </DataRow>
        </Data>
        <Data>
          <DataTitle>Autoresponder</DataTitle>
          <DataRow>
            {responder && <DataText>{responder.name}</DataText>}
            {!responder && <DataText>None</DataText>}
            {responder && (
              <DataButtonWrap>
                <ButtonRoundIcon
                  icon={listIcon}
                  hoverIcon={listWhiteIcon}
                  background={css.BRAND_YELLOW}
                  hoverBackground={css.BRAND_GREEN}
                  onClick={() =>
                    navigate(
                      `/autoresponder?id=80f2dd44-039e-4b54-962b-e05d0a078c62&from=lead-detail&leadId=${id}`
                    )
                  }
                  buttonSize="24px"
                  iconSize="11px"
                />
              </DataButtonWrap>
            )}
          </DataRow>
        </Data>
      </DataColumn>
      <ControlsColumn>
        <ButtonRoundIcon
          icon={binIcon}
          hoverIcon={binWhiteIcon}
          background={css.BRAND_YELLOW}
          hoverBackground={css.NEGATIVE_RED}
          onClick={onDeleteClick}
          buttonSize="34px"
          iconSize="15px"
          disabled={loadingDelete}
        />
      </ControlsColumn>
    </LeadDetail>
  );
};
